import { Dispatch, SetStateAction, useCallback, useState } from 'react';

/**
 * Boolean som överlever sidladdning via localStorage.
 *
 * localStorage lagrar bara strängar, så värdet skrivs som 'true'/'false' och tolkas tillbaka vid
 * läsning. Saknas nyckeln används defaultValue. Läsningen sker i en lat initierare så att den bara
 * körs vid första renderingen.
 *
 * Både läsning och skrivning är inslagna i try/catch: localStorage kastar i blockerat läge och i
 * vissa privatlägen. Då faller vi tillbaka på defaultValue och värdet lever bara för sessionen -
 * utan det kraschar varje komponent som använder hooken redan vid render.
 *
 * Settern tar samma form som Reacts egen, alltså både ett värde och en uppdaterarfunktion.
 * Skrivningen sker inuti uppdateraren eftersom föregående värde behövs för funktionsformen; det
 * ger en dubbelskrivning i StrictMode, vilket är ofarligt då skrivningen är idempotent.
 *
 * Värdet delas inte mellan komponenter - varje anropare har eget state. Två komponenter som läser
 * samma nyckel synkas alltså inte i realtid.
 */
export const usePersistedBoolean = (key: string, defaultValue = false): [boolean, Dispatch<SetStateAction<boolean>>] => {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? defaultValue : stored === 'true';
    } catch {
      return defaultValue;
    }
  });

  const setPersisted = useCallback<Dispatch<SetStateAction<boolean>>>(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        try {
          localStorage.setItem(key, String(resolved));
        } catch {
          // Blockerad storage - värdet lever bara för den här sessionen
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setPersisted];
};
